/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { jsx, cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import AdminPermissionLock from '@jira/illustrations__illustrations/src/ui/adg4/jira/spots/other/components/admin-permission-lock/AdminPermissionLockIllustration.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { solutionComposerAnalyticsAttributes } from './getHasCMPCreatePermissionFromProjectTemplates.tsx';

const contactAdminLink = '/jira/contact-administrator';
const permissionModalUIAnalyticsAttributes = {
	location: 'rovoAiProjectPermissionModal',
	...solutionComposerAnalyticsAttributes,
};

const permissionModalTitle = defineMessage({
	id: 'servicedesk-solution-composer.rovo-ai-project-modal-listener.permission-modal-title',
	defaultMessage: 'Only admins can create a space with Rovo',
	description:
		'Title for a modal shown when a user opens Solution Composer from the create space spotlight but does not have company-managed project creation permission.',
});

const permissionModalBody = defineMessage({
	id: 'servicedesk-solution-composer.rovo-ai-project-modal-listener.permission-modal-body',
	defaultMessage:
		'Rovo only supports creating company-managed spaces. Contact your admin to get started.',
	description:
		'Body text for a modal shown when a user opens Solution Composer from the create space spotlight but does not have company-managed project creation permission.',
});

const permissionModalContactAdmin = defineMessage({
	id: 'servicedesk-solution-composer.rovo-ai-project-modal-listener.permission-modal-contact-admin',
	defaultMessage: 'Contact admin',
	description: 'Primary action label linking to the Jira contact administrator page.',
});

const permissionModalClose = defineMessage({
	id: 'servicedesk-solution-composer.rovo-ai-project-modal-listener.permission-modal-close',
	defaultMessage: 'Close',
	description: 'Accessible label for the close button in the no create permission modal.',
});

const permissionModalLoading = defineMessage({
	id: 'servicedesk-solution-composer.rovo-ai-project-modal-listener.permission-modal-loading',
	defaultMessage: 'Loading Solution Composer',
	description:
		'Accessible label for the loading spinner shown while Solution Composer opens or permission is checked.',
});

const styles = cssMap({
	loadingFallback: {
		width: '100%',
		height: '100%',
	},
	modalBackdrop: {
		boxSizing: 'border-box',
		width: '100%',
		height: '100%',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	modalFrame: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		width: '100%',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	modalHeader: {
		boxSizing: 'border-box',
		position: 'relative',
		zIndex: 1,
		flexShrink: 0,
		paddingInline: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
	},
	modalHeaderRow: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'end',
		width: '100%',
		height: '100%',
		paddingInline: token('space.150'),
		paddingBlock: token('space.050'),
	},
	modalRoot: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: 'auto',
		minHeight: 0,
		flexGrow: 1,
	},
	// Matches the fixed Figma content frame; there is no ADS token for this modal content width.
	content: {
		boxSizing: 'border-box',
		width: '640px',
		maxWidth: '100%',
		paddingTop: token('space.600'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.600'),
	},
	copyWrapper: {
		width: '100%',
	},
});

export function RovoAiProjectModalLoadingFallback(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Flex alignItems="center" justifyContent="center" xcss={styles.loadingFallback}>
			<Spinner size="large" label={formatMessage(permissionModalLoading)} />
		</Flex>
	);
}

function PermissionModalFrame({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId: string;
}): React.JSX.Element {
	return (
		<Box xcss={styles.modalBackdrop} testId={testId}>
			<Box xcss={styles.modalFrame}>{children}</Box>
		</Box>
	);
}

export function RovoAiProjectPermissionModal({
	onClose,
}: {
	onClose: () => void;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				attributes: permissionModalUIAnalyticsAttributes,
			}),
			'closeButton',
		);
		onClose();
	}, [createAnalyticsEvent, onClose]);

	const handleContactAdminClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				attributes: permissionModalUIAnalyticsAttributes,
			}),
			'contactAdminLink',
		);
	}, [createAnalyticsEvent]);

	return (
		<>
			<FireScreenAnalytics attributes={solutionComposerAnalyticsAttributes} />
			<PermissionModalFrame testId="solution-composer.permission-modal">
				<Box xcss={styles.modalHeader}>
					<Box xcss={styles.modalHeaderRow}>
						<Inline alignBlock="center" space="space.100">
							<IconButton
								icon={CrossIcon}
								label={formatMessage(permissionModalClose)}
								appearance="default"
								shape="circle"
								isTooltipDisabled={false}
								onClick={handleClose}
								testId="solution-composer.permission-modal-close-button"
							/>
						</Inline>
					</Box>
				</Box>
				<Stack xcss={styles.modalRoot}>
					<Stack alignInline="center" space="space.800" xcss={styles.content}>
						{/* Figma provides this lock at a fixed 84x117 aspect ratio with no ADS size token equivalent. */}
						<AdminPermissionLock alt="" width="84" height="117" />
						<Stack alignInline="center" space="space.300" xcss={styles.copyWrapper}>
							<Stack alignInline="center" space="space.200">
								<Heading size="large">{formatMessage(permissionModalTitle)}</Heading>
								<Text align="center">{formatMessage(permissionModalBody)}</Text>
							</Stack>
							<Inline alignInline="center">
								<LinkButton
									appearance="primary"
									href={contactAdminLink}
									target="_blank"
									rel="noopener noreferrer"
									iconAfter={LinkExternalIcon}
									onClick={handleContactAdminClick}
								>
									{formatMessage(permissionModalContactAdmin)}
								</LinkButton>
							</Inline>
						</Stack>
					</Stack>
				</Stack>
			</PermissionModalFrame>
		</>
	);
}
